import { useState, useEffect } from "react";
import { Bot, Copy, ExternalLink, KeyRound, Loader2, RefreshCw, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";

function copyText(text) {
  const done = () => toast.success("Copiado!");
  try {
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(text).then(done).catch(() => fallback(text, done));
    } else fallback(text, done);
  } catch { fallback(text, done); }
}
function fallback(text, done) {
  try {
    const ta = document.createElement("textarea");
    ta.value = text; ta.style.position = "fixed"; ta.style.opacity = "0";
    document.body.appendChild(ta); ta.focus(); ta.select();
    document.execCommand("copy"); document.body.removeChild(ta); done();
  } catch {}
}

const STEPS = [
  "No Claude, abra Configurações → Conectores (Customize → Connectors).",
  'Clique em "+" e escolha "Adicionar conector personalizado" (Add custom connector).',
  'Cole o link de conexão abaixo no campo URL e dê o nome "VULPIX".',
  'Clique em "Adicionar" e depois em "Conectar".',
  'Para usar numa conversa: clique no "+" do chat → Conectores → ligue o VULPIX.',
];

export default function AdminMcp() {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [regen, setRegen] = useState(false);
  const [confirmRegen, setConfirmRegen] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/admin/mcp-config").then(({ data }) => setCfg(data))
      .catch((e) => toast.error(apiError(e))).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const doRegen = async () => {
    setConfirmRegen(false);
    setRegen(true);
    try {
      const { data } = await api.post("/admin/mcp-regenerate");
      setCfg(data);
      toast.success("Novo link gerado. O link anterior deixou de funcionar.");
    } catch (e) { toast.error(apiError(e)); }
    finally { setRegen(false); }
  };

  if (loading) return <div className="text-muted-foreground">Carregando...</div>;

  return (
    <div className="max-w-2xl space-y-6" data-testid="admin-mcp">
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
          <Bot className="h-5 w-5" />
        </div>
        <div>
          <h2 className="font-heading text-xl font-bold tracking-tight sm:text-2xl">Conectar ao Claude (MCP)</h2>
          <p className="text-sm text-muted-foreground">Dê ao seu Claude acesso administrativo ao VULPIX por um único link.</p>
        </div>
      </div>

      {/* Link de conexão */}
      <div className="rounded-lg border border-border bg-card p-5">
        <div className="flex items-center justify-between">
          <h3 className="font-heading font-semibold">Link de conexão</h3>
          {cfg?.enabled
            ? <Badge variant="outline" className="gap-1 border-green-500/30 bg-green-500/15 text-green-600 dark:text-green-400" data-testid="mcp-enabled"><ShieldCheck className="h-3 w-3" /> Ativo</Badge>
            : <Badge variant="destructive" data-testid="mcp-disabled">Desativado</Badge>}
        </div>
        <p className="mt-1 text-sm text-muted-foreground">
          O token já vai embutido no link — não precisa configurar cabeçalhos. Mantenha este link <b>secreto</b>: quem tiver ele terá poder total de admin.
        </p>

        {!cfg?.base && (
          <p className="mt-3 rounded-md bg-amber-500/10 p-3 text-xs text-amber-600 dark:text-amber-400">
            Defina a <b>URL base pública (produção)</b> em Configurações para gerar o link de produção. Enquanto isso, o link usa o ambiente atual.
          </p>
        )}

        <div className="mt-4 space-y-2">
          <Label>Link para colar no Claude</Label>
          <div className="flex gap-2">
            <Input readOnly value={cfg?.connect_url || ""} data-testid="mcp-connect-url" className="font-mono text-xs" />
            <Button variant="outline" onClick={() => copyText(cfg?.connect_url || "")} data-testid="mcp-copy-url" className="shrink-0 gap-2">
              <Copy className="h-4 w-4" /> Copiar
            </Button>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          <Button variant="destructive" onClick={() => setConfirmRegen(true)} disabled={regen} data-testid="mcp-regen-btn" className="gap-2">
            {regen ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Gerar novo link
          </Button>
          <Button variant="ghost" onClick={load} className="gap-2"><RefreshCw className="h-4 w-4" /> Recarregar</Button>
        </div>
        <p className="mt-2 text-xs text-muted-foreground">
          Gerar um novo link <b>revoga o anterior</b> imediatamente. Use isso se o link vazar.
        </p>
      </div>

      {/* Passo a passo */}
      <div className="rounded-lg border border-border bg-card p-5">
        <h3 className="font-heading font-semibold">Como conectar no Claude</h3>
        <ol className="mt-3 space-y-3">
          {STEPS.map((s, i) => (
            <li key={i} className="flex gap-3 text-sm" data-testid={`mcp-step-${i + 1}`}>
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-bold text-primary">{i + 1}</span>
              <span className="text-muted-foreground">{s}</span>
            </li>
          ))}
        </ol>
        <p className="mt-4 rounded-md bg-muted/50 p-3 text-xs text-muted-foreground">
          Conectores personalizados funcionam nos planos Free (1 conector), Pro e Max. O Claude conecta pela nuvem da Anthropic,
          então o link precisa ser <b>público</b> — use a URL de produção após o deploy.
        </p>
      </div>

      {/* Alternativa por header */}
      <div className="rounded-lg border border-border bg-card p-5">
        <div className="flex items-center gap-2">
          <KeyRound className="h-4 w-4 text-muted-foreground" />
          <h3 className="font-heading font-semibold">Alternativa: URL base + cabeçalho</h3>
        </div>
        <p className="mt-1 text-sm text-muted-foreground">Se o seu Claude oferecer o campo "Request headers", você pode usar a URL sem o token e passar o token no cabeçalho.</p>
        <div className="mt-4 grid grid-cols-1 gap-3">
          <div className="space-y-2">
            <Label>URL</Label>
            <div className="flex gap-2">
              <Input readOnly value={cfg?.header_url || ""} data-testid="mcp-header-url" className="font-mono text-xs" />
              <Button variant="outline" onClick={() => copyText(cfg?.header_url || "")} className="shrink-0 gap-2"><Copy className="h-4 w-4" /> Copiar</Button>
            </div>
          </div>
          <div className="space-y-2">
            <Label>Cabeçalho Authorization</Label>
            <div className="flex gap-2">
              <Input readOnly value={cfg?.header_value || ""} data-testid="mcp-header-value" className="font-mono text-xs" />
              <Button variant="outline" onClick={() => copyText(cfg?.header_value || "")} className="shrink-0 gap-2"><Copy className="h-4 w-4" /> Copiar</Button>
            </div>
          </div>
        </div>
      </div>

      <AlertDialog open={confirmRegen} onOpenChange={setConfirmRegen}>
        <AlertDialogContent data-testid="mcp-regen-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Gerar um novo link de conexão?</AlertDialogTitle>
            <AlertDialogDescription>
              O link atual deixará de funcionar imediatamente. Você precisará atualizar o conector no Claude com o novo link.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="mcp-regen-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={doRegen} data-testid="mcp-regen-confirm">Gerar novo link</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
